import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { post_ForgotPassword } from '../../services/ForgotPassword';
import EmailValidatedComponent from '../EmailValidatedComponent';
import colorConfigs from '../../configs/colorConfigs';
import { getTenantByEmailAddress } from '../../services/Tenant';
import { checkServerHealth, checkTicketServerHealth, HealthCheck, TenantHealthCheck } from '../../services/Health';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [userData, setUserData] = useState<any>({
        emailAddress: "",
    });
    const [message, setMessage] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        wakeUpServer();
    }, []);

    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const wakeUpServer = async () => {
        try {
            const ticketServerAlive = await checkTicketServerHealth();
            if (ticketServerAlive) {
                console.log("Ticket Server is alive");
            } else {
                console.log("Ticket Server unavailable");
            }
        } catch {
            console.log("Ticket Server unavailable");
        }
    }

    const waitForServer = async (healthCheck: HealthCheck, serverName: string): Promise<boolean> => {
        const delayTime = 5000;

        while (true) {
            try {
                const isAlive = await healthCheck();

                if (isAlive) {
                    console.log(`${serverName} is alive`);
                    return true;
                }

                console.log(`${serverName} not ready, retrying...`);
            } catch (error) {
                console.log(
                    `${serverName} health check error, retrying...`,
                    error
                );
            }

            await delay(delayTime);
        }
    }

    const waitForTenantServer = async (healthCheck: TenantHealthCheck, serverName: string, tenantId: string): Promise<boolean> => {
        const delayTime = 5000;

        while (true) {
            try {
                const isAlive = await healthCheck(tenantId);

                if (isAlive) {
                    console.log(`${serverName} is alive`);
                    return true;
                }

                console.log(`${serverName} not ready, retrying...`);
            } catch (error) {
                console.log(
                    `${serverName} health check error, retrying...`,
                    error
                );
            }

            await delay(delayTime);
        }
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isProcessing) return;

        setIsProcessing(true);
        setMessage('');
        setErrorMessage('');

        try {
            const ticketServerAlive = await waitForServer(checkTicketServerHealth, "Ticket Server");

            if (!ticketServerAlive) {
                setErrorMessage("Ticket Server unavailable");
                setIsProcessing(false);
                return;
            }

            let tenantId: string = "";
            try {
                tenantId = await getTenantByEmailAddress(userData?.emailAddress);

                if (!tenantId || tenantId === "00000000-0000-0000-0000-000000000000") {
                    setErrorMessage('Your email address does not exist.');
                    setIsProcessing(false);
                    return;
                }
            } catch (err: any) {
                setErrorMessage('Your email address does not exist.');
                setIsProcessing(false);
                console.error(err);
                return;
            }

            const serverAlive = await waitForTenantServer(checkServerHealth, "Server", tenantId);

            if (!serverAlive) {
                setErrorMessage("Server unavailable");
                setIsProcessing(false);
                return;
            }

            await attemptToChangePassword(tenantId);
        } catch (error) {
            console.error("Login process failed:", error);
        } finally {
            setIsProcessing(false);
        }
    }

    const attemptToChangePassword = async (tenantId: string) => {
        post_ForgotPassword(userData?.emailAddress, tenantId)
            .then((res) => {
                if (res === userData.emailAddress) {
                    setMessage(`We've emailed a link to ${userData.emailAddress} to let you update your password. 
                        If you don't see the email, be sure to check your spam and junk mail folders.`);
                    setIsSuccess(true);
                } else {
                    setErrorMessage('Your email address does not exist.');
                }
                setIsProcessing(false);
                setUserData({ emailAddress: '' });
            })
            .catch((error) => {
                if (error.response && error.response.status === 500) {
                    setErrorMessage('There is some problem occurring. Please try again later.');
                } else {
                    setErrorMessage('Your email address does not exist.');
                }
                console.error(error);
                setIsProcessing(false);
            });
    }

    return (
        <div style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f7fafc"
        }}>
            <div style={{
                backgroundColor: "white",
                padding: "24px",
                borderRadius: "8px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                width: "100%",
                maxWidth: "400px"
            }}>
                <h2 style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    marginBottom: "20px",
                    textAlign: "center"
                }}>Forgot Password</h2>
                {!isSuccess ? (
                    <form onSubmit={handleSubmit}>
                        <div style={{ marginBottom: "16px" }}>
                            <label style={{ fontSize: '12px', fontWeight: '500', color: '#667085' }}>
                                Enter Email Address
                            </label>
                            <EmailValidatedComponent
                                text={userData.emailAddress}
                                setText={(value) => setUserData({ ...userData, emailAddress: value })}
                                required={true} />
                        </div>
                        <button
                            type="submit"
                            disabled={isProcessing}
                            style={{
                                width: "100%",
                                backgroundColor: isProcessing ? "#a0aec0" : colorConfigs.topbar.bg,
                                color: "white",
                                fontWeight: "bold",
                                padding: "8px 16px",
                                borderRadius: "4px",
                                cursor: isProcessing ? "not-allowed" : "pointer",
                                outline: "none",
                                boxShadow: isProcessing ? "none" : "0 2px 4px rgba(0, 0, 0, 0.1)",
                                transition: "background-color 0.3s"
                            }}
                        >
                            {isProcessing ? 'Processing...' : 'Reset Password'}
                        </button>
                    </form>
                ) : (
                    <div style={{ textAlign: "center" }}>
                        <div style={{ marginTop: "16px", color: "#667085" }}>{message}</div>
                        <button onClick={() => navigate("/login")}
                            style={{
                                marginTop: "16px",
                                backgroundColor: colorConfigs.topbar.bg,
                                color: "white",
                                fontWeight: "bold",
                                padding: "8px 16px",
                                borderRadius: "4px",
                                cursor: "pointer",
                                outline: "none",
                                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                transition: "background-color 0.3s"
                            }}
                        >
                            Go back to sign in
                        </button>
                    </div>
                )}
                {errorMessage && (
                    <div style={{
                        marginTop: "16px",
                        textAlign: "center",
                        color: "#e53e3e"
                    }}>
                        {errorMessage}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ForgotPassword;